import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatParty, isLocationType } from '@/venue/venue';
import type { ActiveSession, FloorLocation, FloorSession } from './api';
import { Amount, EdgeBar, Notice } from './parts';
import { OpenSessionSheet } from './sheets';
import { useActiveSessions, useFloor, useReadyOrders, useStaffLive } from './queries';
import {
  assistanceLabel,
  formatDuration,
  HELP_REQUESTED,
  locationText,
  seatedText,
} from './wording';
import { PageHeader } from '@/components/PageHeader';
import { SectionHeading } from '@/components/SectionHeading';
import { cardClasses } from '@/components/Card';

function partyText(location: FloorLocation, session: FloorSession): string {
  return isLocationType(location.location_type)
    ? formatParty(location.location_type, session.party_display_name, session.party_size)
    : session.party_display_name;
}

/**
 * One seated party. The place and the time open share the first line, the party and the bill total (the
 * backend's figure) the second, and the chips have a row of their own. An amber bar on the left edge means
 * the party asked for help.
 */
function SessionLink({
  location,
  session,
  summary,
  hasReady,
}: {
  location: FloorLocation;
  session: FloorSession;
  summary: ActiveSession | undefined;
  /** The session has an order the kitchen finished (from the ready list). */
  hasReady: boolean;
}) {
  const where = locationText(location.location_type, location.location_label);
  const help = summary?.assistance_request_types ?? [];
  // Named chips only for request types the backend listed; the flag alone is a neutral chip.
  const helpChips = session.has_active_assistance
    ? help.length > 0
      ? [...new Set(help.map(assistanceLabel))]
      : [HELP_REQUESTED]
    : [];
  const chips =
    Boolean(summary?.cart.has_items) ||
    session.has_pending_orders ||
    hasReady ||
    helpChips.length > 0;
  return (
    <Link
      to={`/staff/sessions/${session.session_id}`}
      className={cn(
        cardClasses({ padding: 'none' }),
        'relative flex min-h-16 w-full flex-col gap-1 overflow-hidden py-3 pr-3 pl-4 text-ink no-underline hover:bg-surface',
      )}
    >
      <EdgeBar tone={session.has_active_assistance ? 'attention' : undefined} />
      <span className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 text-base font-bold break-words">{where}</span>
        <span className="shrink-0 text-sm text-muted tabular-nums">
          {formatDuration(session.duration_minutes)}
        </span>
      </span>
      <span className="flex items-baseline justify-between gap-3 text-sm text-muted">
        <span className="min-w-0 break-words">{partyText(location, session)}</span>
        {session.bill_total > 0 && (
          // The session's bill total as the backend reports it, so the waiter can answer "what's my bill?".
          <span className="shrink-0">
            Bill total{' '}
            <Amount minor={session.bill_total} className="font-bold text-ink tabular-nums" />
          </span>
        )}
      </span>
      {chips && (
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
          {summary?.cart.has_items && (
            <Badge tone="neutral">{summary.cart.total_quantity} in cart</Badge>
          )}
          {session.has_pending_orders && <Badge tone="active">In the kitchen</Badge>}
          {hasReady && <Badge tone="success">Ready to serve</Badge>}
          {helpChips.map((chip) => (
            <Badge key={chip} tone="attention">
              {chip}
            </Badge>
          ))}
        </span>
      )}
    </Link>
  );
}

export function FloorPage() {
  useStaffLive();
  const navigate = useNavigate();
  const floor = useFloor();
  const sessions = useActiveSessions();
  const ready = useReadyOrders();
  const [showVacant, setShowVacant] = useState(false);
  const [opening, setOpening] = useState<FloorLocation | null>(null);

  const summaries = useMemo(
    () => new Map((sessions.data ?? []).map((s) => [s.session_id, s])),
    [sessions.data],
  );
  const readySessions = useMemo(
    () => new Set((ready.data ?? []).map((o) => o.session_id)),
    [ready.data],
  );
  const locations = useMemo(() => floor.data ?? [], [floor.data]);
  const cinema = locations.length > 0 && locations.every((l) => l.location_type === 'SEAT');
  const seated = locations.reduce((n, l) => n + l.active_sessions.length, 0);
  const hasSessions = seated > 0;
  const groups = useMemo(() => {
    const shown = locations.filter((l) => l.active_sessions.length > 0 || showVacant);
    const byZone = new Map<string, FloorLocation[]>();
    for (const location of shown) {
      const zone = location.zone_name ?? 'Other';
      byZone.set(zone, [...(byZone.get(zone) ?? []), location]);
    }
    return [...byZone.entries()];
  }, [locations, showVacant]);

  return (
    <section aria-labelledby="floor-title" className="flex flex-col gap-4">
      <PageHeader
        id="floor-title"
        title="Floor"
        subtitle={floor.data ? seatedText(seated, cinema) : undefined}
        actions={
          <>
            <Button
              variant="secondary"
              aria-pressed={showVacant}
              className={showVacant ? 'border-primary bg-primary-soft text-primary' : undefined}
              onClick={() => setShowVacant((value) => !value)}
            >
              {showVacant && <Icon name="check" size={16} />}
              {showVacant ? 'Showing vacant' : 'Show vacant'}
            </Button>
            <Button
              variant="secondary"
              loading={floor.isFetching && !floor.isPending}
              onClick={() => {
                void floor.refetch();
                void sessions.refetch();
              }}
            >
              Refresh
            </Button>
          </>
        }
      />

      {floor.isPending && (
        <LoadingRegion slowHint label="Loading the floor">
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        </LoadingRegion>
      )}
      {floor.isError && !floor.data && (
        <ErrorState error={floor.error} onRetry={() => void floor.refetch()} />
      )}
      {floor.isError && floor.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void floor.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the floor. Showing the last list received.
        </Notice>
      )}
      {floor.data && !hasSessions && groups.length === 0 && (
        <EmptyState
          icon="floor"
          title="No active sessions"
          message="Sessions appear here as soon as guests are seated."
        />
      )}
      {groups.map(([zone, items]) => (
        <section key={zone} aria-labelledby={`zone-${zone}`} className="flex flex-col gap-2">
          <SectionHeading id={`zone-${zone}`}>{zone}</SectionHeading>
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
            {items.map((location) =>
              location.active_sessions.length > 0 ? (
                location.active_sessions.map((session) => (
                  <li key={session.session_id}>
                    <SessionLink
                      location={location}
                      session={session}
                      summary={summaries.get(session.session_id)}
                      hasReady={readySessions.has(session.session_id)}
                    />
                  </li>
                ))
              ) : (
                <li
                  key={location.location_id}
                  className="flex min-h-16 items-center justify-between gap-3 rounded-surface border border-dashed border-line-strong px-4 py-2"
                >
                  <span className="text-base text-muted">
                    {locationText(location.location_type, location.location_label)} · Vacant
                  </span>
                  <Button
                    variant="secondary"
                    aria-label={`Open session at ${locationText(location.location_type, location.location_label)}`}
                    onClick={() => setOpening(location)}
                  >
                    <Icon name="plus" size={16} />
                    Open session
                  </Button>
                </li>
              ),
            )}
          </ul>
        </section>
      ))}
      {opening && (
        <OpenSessionSheet
          location={opening}
          onClose={() => setOpening(null)}
          onOpened={(sessionId) => {
            setOpening(null);
            navigate(`/staff/sessions/${sessionId}`);
          }}
        />
      )}
    </section>
  );
}
